import { useMemo } from 'react'
import { Flow } from '../types'
import { FLOW_CONFIG, LICENSE_TYPE_NAMES } from '../constants'

interface LicenseInfo {
  licenseType?: string
  endsAt?: number
}

// Premium is the pre-Pro product. Its license types have no "pro" suffix.
const PREMIUM_TYPES = ['classroom', 'school', 'homeschool']
// Pro is the current product; its license types carry the "pro" suffix.
const PRO_TYPES = ['classroompro', 'schoolpro', 'homeschoolpro']

export interface AccountStatus {
  // The account has held a Premium license before (active or lapsed), so we still offer Premium.
  hadPremium: boolean
  // The account is already on Pro, so there is nothing left to upgrade to.
  hasActivePro: boolean
  // Label and tone for the status pill.
  plan: { label: string; expired: boolean; active: boolean }
}

/** Reads the licenses embedded on the teacher record to describe the account's plan status. */
export default function useAccountStatus(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  primaryFlow: Flow
): AccountStatus {
  return useMemo(() => {
    const infos: LicenseInfo[] = currentTeacher
      ? Object.values({
          ...(currentTeacher.adminLicenseInfos || {}),
          ...(currentTeacher.licenseInfos || {}),
        }).filter(Boolean)
      : []

    const hadPremium = infos.some((li) =>
      PREMIUM_TYPES.includes(li.licenseType ?? '')
    )
    const hasActivePro = infos.some(
      (li) =>
        PRO_TYPES.includes(li.licenseType ?? '') &&
        (li.endsAt ?? 0) > Date.now()
    )

    if (infos.length) {
      // The license that runs longest is the one worth reporting.
      const latest = infos.reduce((a, b) =>
        (b.endsAt ?? 0) > (a.endsAt ?? 0) ? b : a
      )
      const name = LICENSE_TYPE_NAMES[latest.licenseType ?? ''] || 'Your plan'
      if ((latest.endsAt ?? 0) <= Date.now()) {
        return {
          hadPremium,
          hasActivePro,
          plan: { label: `${name} expired`, expired: true, active: false },
        }
      }
      // Still in force — name the plan instead of calling the account free.
      return {
        hadPremium,
        hasActivePro,
        plan: { label: name, expired: false, active: true },
      }
    }

    return {
      hadPremium,
      hasActivePro,
      plan: {
        label: `Free ${FLOW_CONFIG[primaryFlow].freeNoun}`,
        expired: false,
        active: false,
      },
    }
  }, [currentTeacher, primaryFlow])
}
